import React, { useCallback, useMemo, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Header, FlexibleLinkButton } from "@pl/paylican-ui-kit";
import { useKeycloakContext, PrivateAccess, hasRoles } from "@wf/keycloak-axios-provider";
import { useChat } from "@wf/client-chat";

import ErrorModal from "../../Modals/ErrorModal";
import { useInitApp } from "../../../hooks";
import PrepaymentAccess from "../../Prepayment/PrepaymentAccess";
import * as pathnames from "../../../constants/pathnames";
import useIsLinkSelected from "./useIsLinkSelected";
import { ENABLE_INVOICE_PROCESSING, ENABLE_INVOICE_CREATION } from "../../../constants/envVariables";

import styles from "./Layout.module.scss";
import ngTheme from "./LayoutNG.module.scss";
import StLogo from "../../../public/logoSt.png";

const themeType = process.env?.REACT_APP_APP_THEME?.toString();

const theme = themeType ? ngTheme : undefined;

const languages = [
  { displayName: "Français", name: "fr" },
  { displayName: "English", name: "en" },
  { displayName: "Español", name: "es" }
];
const settingsUrl = process.env.REACT_APP_SETTINGS_CLIENT_URL;
const enableInvoiceProcessing = ENABLE_INVOICE_PROCESSING === "true";
const enableInvoiceCreation = ENABLE_INVOICE_CREATION === "true";

const innerRoles = ["SUPER_HELP_DESK", "HELP_DESK", "MERCHANT", "BENEFICIARY"];
let prevUrl = null;
const Layout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locationHook = useLocation();
  const keycloakContext = useKeycloakContext();

  const user = useMemo(() => keycloakContext.getUserData(), [keycloakContext]);
  const roles = keycloakContext.getUserData()?.roles["invoice-system-client"] ?? [];
  const showDashboard = !hasRoles({ "invoice-system-client": ["BENEFICIARY"] }, keycloakContext.getUserData()?.roles);

  useChat({ user, id: process.env.REACT_APP_CHAT_ID, isDisabled: !process.env.REACT_APP_CHAT_ID });
  const isInnerUser = useMemo(() => {
    return roles.some(r => innerRoles.includes(r));
  }, [roles]);

  useEffect(() => {
    prevUrl = location.pathname + location.search;
    return () => {
      location.prevUrl = prevUrl;
    };
  }, [locationHook]);

  const isAppInitialized = isInnerUser ? true : useInitApp();

  const handleLanguageChange = useCallback(
    current => {
      i18n.changeLanguage(current);
    },
    [i18n]
  );

  const handleLogoClick = useCallback(() => {
    if (process.env.REACT_APP_HEADER_LOGO_GO_TO_URL) {
      window.location.href = process.env.REACT_APP_HEADER_LOGO_GO_TO_URL;
    } else {
      navigate("/");
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    // eslint-disable-next-line no-undef
    window.history.replaceState({}, document.title, process.env.REACT_APP_PUBLIC_PATH);
    keycloakContext.logout();
  }, [keycloakContext]);

  const goToConfigs = useCallback(() => {
    // eslint-disable-next-line no-undef
    window.location.href = `${settingsUrl}?lng=${i18n.language}`;
  }, [i18n.language]);

  const {
    isDashboardSelected,
    isPaymentsSelected,
    isInvoicesSelected,
    isPrepaymentSelected,
    isInvoiceProcessingSelected,
    isInvoiceCreationSelected
  } = useIsLinkSelected();

  return (
    <>
      <Header
        onLogoClick={handleLogoClick}
        testEnvText={process.env.REACT_APP_ENVIRONMENT_NAME}
        logo={themeType === "ST" ? StLogo : process.env?.REACT_APP_HEADER_LOGO_URL}
        theme={theme}
      >
        <Header.Links>
          <Header.Links.Item selected={isDashboardSelected}>
            {showDashboard && (
              <Link key="dashboard" to={pathnames.dashboard} className={styles.link}>
                {t("dashboardLink")}
              </Link>
            )}
          </Header.Links.Item>
          <Header.Links.Item selected={isInvoicesSelected}>
            <Link key="invoices" to={pathnames.main} className={styles.link}>
              {t("invoicesLink")}
            </Link>
          </Header.Links.Item>
          {enableInvoiceCreation && (
            <PrivateAccess roles={{ "invoice-system-client": ["MERCHANT", "SUPER_MERCHANT"] }}>
              <Header.Links.Item selected={isInvoiceCreationSelected}>
                <Link key="paymentsList" to={pathnames.invoiceCreation} className={styles.link}>
                  {t("Invoice Creation")}
                </Link>
              </Header.Links.Item>
            </PrivateAccess>
          )}
          <PrivateAccess
            roles={{
              "invoice-system-client": [
                "COMPANY",
                "INDIVIDUAL",
                "HELP_DESK",
                "SUPER_HELP_DESK",
                "CUSTOMER_MERCHANT_HELPDESK"
              ]
            }}
          >
            <Header.Links.Item selected={isPaymentsSelected}>
              <Link key="paymentsList" to={pathnames.payments} className={styles.link}>
                {t("paymentsLink")}
              </Link>
            </Header.Links.Item>
          </PrivateAccess>
          <PrepaymentAccess>
            <Header.Links.Item selected={isPrepaymentSelected}>
              <Link key="prepayment" to={pathnames.prepayment} className={styles.link}>
                {t("Prepayment")}
              </Link>
            </Header.Links.Item>
          </PrepaymentAccess>
          {enableInvoiceProcessing && (
            <PrivateAccess roles={{ "invoice-system-client": ["SUPER_HELP_DESK"] }}>
              <Header.Links.Item selected={isInvoiceProcessingSelected}>
                <Link key="invoiceProcessing" to={pathnames.invoiceProcessing} className={styles.link}>
                  {t("Processing")}
                </Link>
              </Header.Links.Item>
            </PrivateAccess>
          )}
        </Header.Links>
        <Header.LanguageSelect
          languages={languages}
          selected={i18n.language || "en"}
          handleChange={handleLanguageChange}
        />
        <Header.UserMenu user={user} handleLogout={handleLogout} theme={theme}>
          {settingsUrl && (
            <Header.UserMenu.Item>
              <FlexibleLinkButton onClick={goToConfigs}>
                <FontAwesomeIcon icon={["fal", "cog"]} className={styles.linkIcon} />
                {t("Settings")}
              </FlexibleLinkButton>
            </Header.UserMenu.Item>
          )}
        </Header.UserMenu>
      </Header>
      {isAppInitialized && (
        <main id="mainCont" className={styles.main} style={{ maxWidth: "1366px" }}>
          <Outlet />
        </main>
      )}
      <ErrorModal />
    </>
  );
};

export default React.memo(Layout);
